import React, { useEffect, useMemo } from 'react';
import { Alert, Link } from '@amzn/awsui-components-console';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
	SNOW_NXT_WORKFLOW_ENDPOINT,
	SDS_APIGATEWAY_ENDPOINT,
} from 'src/config';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { useAuthContext } from 'src/providers/AuthContext';
import {
	GetOperationResponse,
	SupportedOperation,
} from '@amzn/snow-data-service-typescript-client';
import { logger } from 'src/utils/logger';
import { ExportOperationAsTaskResponse } from '@amzn/snow-nxt-workflow-service-typescript-client';

import ExportOperationToTaskPageForm from './ExportOperationToTaskPageForm';
import {
	ExportTaskFormData,
	mapAttributeDataToSnowAttribute,
	DEFAULT_EXPORT_TASK_DATA,
} from './ExportTaskFormGenerationUtils';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { useStore } from 'src/stores/store';

export interface ExportOperationToTaskPageProps {
	onCancel: () => void;
	operationId: string;
	operation?: SupportedOperation;
}

const ExportOperationToTaskPage: React.FC<ExportOperationToTaskPageProps> = ({
	onCancel,
	operationId,
	operation,
}) => {
	const { userName } = useAuthContext();
	const { navigate } = useNavigation();
	const {
		selectedApplication,
		addNavigationError,
	} = useStore();

	const methods = useForm<ExportTaskFormData>({
		defaultValues: {
			...DEFAULT_EXPORT_TASK_DATA,
			operationId: operationId || '',
			owner: userName || '',
		},
	});

	const { setValue } = methods;

	const { result: exportResult, onClickCB: exportOperation } =
		useFetchOnUserActionUsingSDS<ExportOperationAsTaskResponse>();

	const {
		isLoading: isLoadingOperation,
		data: operationData,
		error: operationError,
	} = useFetchForSDS<GetOperationResponse>({
		url: SDS_APIGATEWAY_ENDPOINT,
		path: operationId ? `/operation/${operationId}` : '',
		method: 'GET',
	});

	const combinedOperationData = useMemo(() => {
		return {
			...operationData?.operationMetadata,
			...operation,
		};
	}, [operationData?.operationMetadata, operation]);

	const taskInputs = useMemo(() => {
		return combinedOperationData.taskInputs || [];
	}, [combinedOperationData.taskInputs]);

	const taskOutputs = useMemo(() => {
		return combinedOperationData.taskOutputs || [];
	}, [combinedOperationData.taskOutputs]);

	useEffect(() => {
		if (!combinedOperationData.operationId) return;

		setValue('operationId', combinedOperationData.operationId);
		setValue('operationName', combinedOperationData.operationName || '');
		setValue('description', combinedOperationData.description || '');

		if (combinedOperationData.integrationType) {
			setValue(
				'integrationType',
				combinedOperationData.integrationType as ExportTaskFormData['integrationType']
			);
		}

	}, [combinedOperationData]);

	const submitForm = async (data: ExportTaskFormData) => {
		if (!data.operationId) {
			return;
		}

		const payload = {
			...data,
			taskInput: taskInputs.map(mapAttributeDataToSnowAttribute),
			taskOutput: taskOutputs.map(mapAttributeDataToSnowAttribute),
			user: userName,
			requestId: uuidv4(),
		};

		await exportOperation({
			path: `/operation/export/${payload.operationId}`,
			method: 'PUT',
			body: payload,
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
		})
			.then((response: ExportOperationAsTaskResponse) => {
				logger.info(
					`Successfully exported operation to task. Workflow ID: ${
						response.workflowId || 'N/A'
					}, Task Name: ${response.taskName || data.taskName || 'N/A'}`
				);

				addNavigationError({
					message: (
						<>
							Operation{' '}
							<Link
								variant="primary"
								onFollow={() =>
									navigate(
										ROUTES.APPLICATION_DETAILS,
										`/applicationManagement/${selectedApplication?.applicationId}`
									)
								}
							>
								{formData.operationName}
							</Link>{' '}
							to Task {response.workflowId}: {response.taskName} exported
							successfully!
						</>
					),
					type: 'success',
					timestamp: Date.now(),
				});

				if (selectedApplication?.applicationId) {
					navigate(
						ROUTES.APPLICATION_DETAILS,
						`/applicationManagement/${selectedApplication.applicationId}`
					);
				}
			})
			.catch((error) => {
				logger.error(`Error exporting operation:${error}`);

				addNavigationError({
					message: `Failed to export operation: ${error}`,
					type: 'error',
					timestamp: Date.now(),
				});
			});
	};

	if (operationError && !operation) {
		const errorMessage =
			typeof operationError === 'string'
				? operationError
				: JSON.stringify(operationError);

		return (
			<Alert type="error">
				Unable to load operation data for operation ID: {operationId}
				<br />
				Error: {errorMessage}
				<br />
				<br />
				This may happen if:
				<ul>
					<li>The operation doesn&apos;t exist</li>
					<li>You don&apos;t have permission to access this operation</li>
					<li>There&apos;s a temporary server issue</li>
				</ul>
				Please try navigating back to the operations list and selecting the
				operation again.
			</Alert>
		);
	}

	if (!operationId && !operation) {
		return (
			<Alert type="error">
				No operation selected. Please select an operation from the operations
				list first.
			</Alert>
		);
	}

	const formData = methods.watch();

	return (
		<FormProvider {...methods}>
			<ExportOperationToTaskPageForm
				onCancel={onCancel}
				onSubmit={submitForm}
				operationId={formData.operationId || ''}
				operationName={formData.operationName || ''}
				integrationType={formData.integrationType || ''}
				taskInputs={taskInputs}
				taskOutputs={taskOutputs}
				isLoading={exportResult.isLoading || isLoadingOperation}
				errorMessage={exportResult.error || operationError}
				flashBarItems={exportResult.flashBarItems}
			/>
		</FormProvider>
	);
};

export default ExportOperationToTaskPage;
